import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { adminApi, adminAuth, type AdminOrder, type FollowUp } from '../lib/adminApi';

export function AdminOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<AdminOrder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showResubmit, setShowResubmit] = useState(false);
  const [authCode, setAuthCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState<string | null>(null);
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);

  const refreshFollowUps = (orderId: string) => {
    adminApi.listFollowUps('all', orderId).then((r) => setFollowUps(r.items)).catch(() => {});
  };

  useEffect(() => {
    if (!adminAuth.isLoggedIn()) {
      navigate('/admin/login');
      return;
    }
    if (!id) return;
    let alive = true;
    const tick = async () => {
      try {
        const o = await adminApi.getOrder(id);
        if (!alive) return;
        setOrder(o);
        // If currently in-flight, keep polling.
        if (o.status === 'PAID' || o.status === 'SUBMITTING') {
          setTimeout(tick, 4000);
        }
      } catch (err) {
        if (alive) setError(err instanceof Error ? err.message : 'Lookup failed');
      }
    };
    tick();
    refreshFollowUps(id);
    return () => {
      alive = false;
    };
  }, [id, navigate]);

  async function doResubmit() {
    if (!order) return;
    if (!/^[A-Za-z0-9]{6}$/.test(authCode)) {
      setSubmitMsg('Auth code must be 6 alphanumeric characters');
      return;
    }
    setSubmitting(true);
    setSubmitMsg(null);
    try {
      await adminApi.resubmit(order.id, authCode);
      setSubmitMsg('Resubmission triggered – refreshing status…');
      setShowResubmit(false);
      setAuthCode('');
      // Force a fresh fetch.
      const fresh = await adminApi.getOrder(order.id);
      setOrder(fresh);
    } catch (err) {
      setSubmitMsg(err instanceof Error ? err.message : 'Resubmit failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (error)
    return (
      <AdminLayout>
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      </AdminLayout>
    );
  if (!order)
    return (
      <AdminLayout>
        <div>Loading…</div>
      </AdminLayout>
    );

  const canResubmit = order.status === 'REJECTED' || order.status === 'PAID';

  const upcoming = followUps.filter((f) => f.status === 'PENDING').sort((a, b) => a.dueDate.localeCompare(b.dueDate));

  return (
    <AdminLayout>
      <Link to="/admin" className="text-sm text-brand-700 hover:underline">
        ← All orders
      </Link>

      <div className="mt-4 flex items-start justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            {order.businessDisplayId && (
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 font-mono text-xs font-semibold ring-1 ring-inset ${
                  order.status !== 'PENDING_PAYMENT'
                    ? 'bg-green-100 text-green-800 ring-green-300'
                    : 'bg-amber-100 text-amber-800 ring-amber-300'
                }`}
                title={order.status !== 'PENDING_PAYMENT' ? 'Paid — business reference' : 'Awaiting payment'}
              >
                {order.businessDisplayId}
              </span>
            )}
            <h1 className="text-2xl font-semibold text-slate-900">{order.companyName}</h1>
            {order.archived && (
              <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700">
                Archived
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-500">
            {order.leadDisplayId && (
              <span
                className={`mr-2 font-mono text-xs font-semibold ${
                  order.status !== 'PENDING_PAYMENT' ? 'text-green-700' : 'text-amber-700'
                }`}
              >
                {order.leadDisplayId}
              </span>
            )}
            Company {order.companyNumber} · Order <code className="font-mono text-xs">{order.id}</code>
          </p>
        </div>
        <div className="flex items-start gap-3">
          {(order.businessFilings !== undefined || order.businessLifetimePence !== undefined) && (
            <div className="flex gap-2 text-center text-xs">
              <div className="rounded-lg bg-white px-3 py-2 ring-1 ring-slate-200">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Filings</div>
                <div className="mt-0.5 text-base font-semibold text-slate-900 tabular-nums">
                  {order.businessFilings ?? 0}
                </div>
              </div>
              <div className="rounded-lg bg-white px-3 py-2 ring-1 ring-slate-200">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Lifetime</div>
                <div className="mt-0.5 text-base font-semibold text-slate-900 tabular-nums">
                  £{((order.businessLifetimePence ?? 0) / 100).toFixed(2)}
                </div>
              </div>
            </div>
          )}
          <StatusBadge status={order.status} />
        </div>
      </div>

      {order.archived && (
        <div className="mt-4 rounded-lg bg-slate-100 px-4 py-2 text-sm text-slate-700">
          <strong>This order is archived.</strong>
          {order.archivedAt && (
            <span className="ml-2 text-xs text-slate-500">
              since{' '}
              {new Date(order.archivedAt).toLocaleDateString('en-GB', {
                day: 'numeric', month: 'short', year: 'numeric',
              })}
            </span>
          )}
          {order.archivedReason && (
            <span className="ml-2 text-xs text-slate-500">· {order.archivedReason}</span>
          )}
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_280px]">
        <div>

      <QuickActions order={order} onChange={() => adminApi.getOrder(order.id).then(setOrder)} />

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <InfoCard label="Customer">
          <Row k="Email" v={order.email} />
          <Row k="Director" v={order.directorName} />
        </InfoCard>
        <InfoCard label="Period">
          <Row k="Start" v={order.periodStart} />
          <Row k="End" v={order.periodEnd} />
          <Row k="First filing?" v={order.isFirstFiling ? 'Yes' : 'No'} />
        </InfoCard>
        <InfoCard label="Payment">
          <Row k="Amount" v={`£${(order.amountPence / 100).toFixed(2)}`} />
          <Row k="Stripe session" v={order.stripeSessionId ? order.stripeSessionId.slice(0, 24) + '…' : '–'} mono />
          <Row k="Paid at" v={order.paidAt ? new Date(order.paidAt).toLocaleString('en-GB') : '–'} />
        </InfoCard>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <BalanceSheetCard order={order} onSaved={() => adminApi.getOrder(order.id).then(setOrder)} />
        <InfoCard label="Companies House">
          <Row k="Submission ID" v={order.chSubmissionId || '–'} mono />
          <Row k="Ack number" v={order.chAckNumber || '–'} mono />
          <Row k="Submitted at" v={order.submittedAt ? new Date(order.submittedAt).toLocaleString('en-GB') : '–'} />
          {order.errorReason && (
            <div className="mt-2 rounded-md bg-red-50 p-2 text-xs text-red-700">
              <strong>Rejection reason:</strong>
              <div className="mt-1">{order.errorReason}</div>
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-3">
            <a href={`/api/orders/${order.id}/ixbrl`} target="_blank" rel="noreferrer" className="text-xs text-brand-700 hover:underline">
              Preview iXBRL →
            </a>
            <button
              onClick={() =>
                adminApi.fetchRawXml(order.id).catch((err) =>
                  window.alert(err instanceof Error ? err.message : 'Fetch raw XML failed'),
                )
              }
              className="text-xs text-brand-700 hover:underline"
            >
              View raw XML →
            </button>
          </div>
        </InfoCard>
      </div>

      {canResubmit && (
        <div className="mt-6 rounded-xl bg-white p-5 ring-1 ring-slate-200">
          <h2 className="font-semibold text-slate-900">Resubmit to Companies House</h2>
          <p className="mt-1 text-sm text-slate-600">
            The auth code from the original wizard session has expired. Enter the company's
            6-character Companies House authentication code to retry submission.
          </p>
          {submitMsg && (
            <div className="mt-3 rounded-md bg-amber-50 p-2 text-xs text-amber-800">{submitMsg}</div>
          )}
          {!showResubmit ? (
            <button onClick={() => setShowResubmit(true)} className="btn-primary mt-3">
              Resubmit…
            </button>
          ) : (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input
                value={authCode}
                onChange={(e) => setAuthCode(e.target.value.toUpperCase())}
                placeholder="••••••"
                maxLength={6}
                className="input max-w-[140px] text-center text-lg tracking-[0.4em]"
              />
              <button onClick={doResubmit} disabled={submitting} className="btn-primary">
                {submitting ? 'Submitting…' : 'Confirm resubmit'}
              </button>
              <button onClick={() => setShowResubmit(false)} className="btn-ghost">
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      <FollowUpsSection
        orderId={order.id}
        followUps={followUps}
        onChange={() => refreshFollowUps(order.id)}
      />
        </div>

        {/* Right column – upcoming follow-up dates, stacked top-to-bottom. */}
        <aside className="lg:sticky lg:top-20 lg:self-start">
          <UpcomingDateStack tasks={upcoming.slice(0, 6)} />
        </aside>
      </div>
    </AdminLayout>
  );
}

// ─── Follow-ups section (inline in detail page) ──────────────────────────

function FollowUpsSection({
  orderId,
  followUps,
  onChange,
}: {
  orderId: string;
  followUps: FollowUp[];
  onChange: () => void;
}) {
  const pending = followUps.filter((f) => f.status === 'PENDING').sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  const history = followUps.filter((f) => f.status !== 'PENDING').sort((a, b) => (b.completedAt || '').localeCompare(a.completedAt || ''));

  return (
    <div className="mt-6 rounded-xl bg-white p-5 ring-1 ring-slate-200">
      <h2 className="font-semibold text-slate-900">Follow-ups</h2>
      <p className="mt-1 text-sm text-slate-600">Internal CRM tasks for this order.</p>

      {pending.length > 0 && (
        <>
          <h3 className="mt-5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Pending
          </h3>
          <ul className="mt-2 space-y-2">
            {pending.map((f) => (
              <PendingFollowUpRow key={f.id} fu={f} onChange={onChange} />
            ))}
          </ul>
        </>
      )}

      {history.length > 0 && (
        <>
          <h3 className="mt-5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            History
          </h3>
          <ul className="mt-2 space-y-2">
            {history.map((f) => (
              <CompletedFollowUpRow key={f.id} fu={f} />
            ))}
          </ul>
        </>
      )}

      {pending.length === 0 && history.length === 0 && (
        <p className="mt-5 text-sm text-slate-500">No follow-ups yet. Add one below.</p>
      )}

      <AddFollowUpForm orderId={orderId} onAdded={onChange} />
    </div>
  );
}

function PendingFollowUpRow({ fu, onChange }: { fu: FollowUp; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState('');
  async function markDone() {
    setBusy(true);
    try {
      await adminApi.updateFollowUp(fu.id, { status: 'COMPLETED', completedNote: note || undefined });
      onChange();
    } finally {
      setBusy(false);
      setEditing(false);
      setNote('');
    }
  }
  async function remove() {
    if (!confirm('Delete this follow-up?')) return;
    setBusy(true);
    try {
      await adminApi.deleteFollowUp(fu.id);
      onChange();
    } finally {
      setBusy(false);
    }
  }
  const overdue = fu.dueDate < todayIso();
  return (
    <li className="rounded-lg bg-slate-50 p-3 ring-1 ring-slate-200">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs">
            <span className={`rounded-full px-2 py-0.5 font-medium ${overdue ? 'bg-red-100 text-red-700' : 'bg-brand-100 text-brand-700'}`}>
              {fmtDate(fu.dueDate)}
            </span>
            {fu.autoCreated && (
              <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                AUTO
              </span>
            )}
          </div>
          <p className="mt-2 text-sm text-slate-900">{fu.reason}</p>
        </div>
        <div className="flex flex-col items-end gap-1 text-xs">
          {!editing ? (
            <button onClick={() => setEditing(true)} className="text-brand-700 hover:underline">
              Mark done
            </button>
          ) : (
            <>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What did you do? (optional)"
                className="input !py-1 !text-xs"
              />
              <button onClick={markDone} disabled={busy} className="btn-primary !py-1 !text-xs">
                Confirm
              </button>
            </>
          )}
          <button onClick={remove} disabled={busy} className="text-[11px] text-slate-400 hover:text-red-600">
            Delete
          </button>
        </div>
      </div>
    </li>
  );
}

function CompletedFollowUpRow({ fu }: { fu: FollowUp }) {
  return (
    <li className="rounded-lg bg-white p-3 ring-1 ring-slate-100">
      <div className="flex items-start gap-2 text-xs">
        <span className={`mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full ${fu.status === 'COMPLETED' ? 'bg-green-500' : 'bg-slate-400'}`} />
        <div className="min-w-0 flex-1">
          <div className="text-slate-500">
            {fu.status === 'COMPLETED' ? '✓ done' : 'dismissed'} ·{' '}
            {fu.completedAt
              ? new Date(fu.completedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
              : '–'}
            {fu.autoCreated && <span className="ml-1 text-slate-400">(auto)</span>}
          </div>
          <p className="mt-0.5 text-sm text-slate-700">{fu.reason}</p>
          {fu.completedNote && (
            <p className="mt-0.5 text-xs italic text-slate-500">"{fu.completedNote}"</p>
          )}
        </div>
      </div>
    </li>
  );
}

function AddFollowUpForm({ orderId, onAdded }: { orderId: string; onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [dueDate, setDueDate] = useState(addDaysIso(todayIso(), 7));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit() {
    if (!reason.trim()) return;
    setBusy(true);
    try {
      await adminApi.createFollowUp(orderId, { dueDate, reason: reason.trim() });
      setReason('');
      setOpen(false);
      onAdded();
    } finally {
      setBusy(false);
    }
  }
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="btn-ghost mt-5 text-xs">
        + Add follow-up
      </button>
    );
  }
  return (
    <div className="mt-5 space-y-2 rounded-lg bg-slate-50 p-3 ring-1 ring-slate-200">
      <div className="grid gap-2 sm:grid-cols-[160px_1fr]">
        <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="input !py-1.5 !text-sm" />
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Chase customer about new auth code"
          className="input !py-1.5 !text-sm"
        />
      </div>
      <div className="flex gap-2">
        <button onClick={submit} disabled={busy || !reason.trim()} className="btn-primary !py-1 !text-xs">
          {busy ? 'Adding…' : 'Add'}
        </button>
        <button onClick={() => setOpen(false)} className="btn-ghost !py-1 !text-xs">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Right-side calendar date stack ──────────────────────────────────────

function UpcomingDateStack({ tasks }: { tasks: FollowUp[] }) {
  if (tasks.length === 0) {
    return (
      <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Next follow-ups
        </h3>
        <p className="mt-3 text-xs text-slate-500">No upcoming tasks for this order.</p>
      </div>
    );
  }
  return (
    <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        Next follow-ups
      </h3>
      <ul className="mt-3 space-y-2">
        {tasks.map((t) => (
          <DateChip key={t.id} task={t} />
        ))}
      </ul>
    </div>
  );
}

function DateChip({ task }: { task: FollowUp }) {
  const d = new Date(task.dueDate + 'T00:00:00Z');
  const today = new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00Z');
  const days = Math.round((d.getTime() - today.getTime()) / 86_400_000);

  // Urgency styling. Red = overdue/today, amber = within a week, slate = later.
  let tone = 'bg-slate-50 text-slate-600 ring-slate-200';
  let label = `IN ${days} DAYS`;
  if (days < 0) {
    tone = 'bg-red-50 text-red-700 ring-red-200';
    label = `${-days} DAYS OVERDUE`;
  } else if (days === 0) {
    tone = 'bg-red-50 text-red-700 ring-red-200';
    label = 'TODAY';
  } else if (days === 1) {
    tone = 'bg-amber-50 text-amber-700 ring-amber-200';
    label = 'TOMORROW';
  } else if (days <= 7) {
    tone = 'bg-amber-50 text-amber-700 ring-amber-200';
    label = `IN ${days} DAYS`;
  }

  return (
    <li className={`rounded-lg p-3 ring-1 ring-inset ${tone}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider">{label}</div>
      <div className="mt-0.5 text-xs font-semibold">
        {d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })}
      </div>
      <div className="mt-1 line-clamp-2 text-[11px] font-normal opacity-80">{task.reason}</div>
    </li>
  );
}

// ─── shared utils ────────────────────────────────────────────────────────

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
function addDaysIso(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
function fmtDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  if (Number.isNaN(+d)) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function InfoCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-white p-5 ring-1 ring-slate-200">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</h3>
      <dl className="mt-3 space-y-1.5 text-sm">{children}</dl>
    </div>
  );
}

function Row({ k, v, mono = false, bold = false }: { k: string; v: string; mono?: boolean; bold?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-500">{k}</dt>
      <dd className={`text-right ${mono ? 'font-mono text-xs' : ''} ${bold ? 'font-semibold' : ''}`}>{v}</dd>
    </div>
  );
}

/**
 * Editable balance-sheet card. Read-only view by default; clicking
 * Edit unlocks all seven figure fields inline. Saving PATCHes the
 * new figures to the backend, which also clears the stored iXBRL so
 * the next Resubmit regenerates it from the fresh numbers.
 *
 * Only permitted on non-terminal + rejected orders (PAID, SUBMITTING,
 * REJECTED). ACCEPTED orders lock down because the filing is already
 * on the public register — editing figures there would silently
 * diverge our record from CH's, which is what our Terms clause 4.3
 * explicitly disclaims responsibility for.
 */
function BalanceSheetCard({ order, onSaved }: { order: AdminOrder; onSaved: () => void }) {
  const editable = order.status !== 'ACCEPTED' && !order.archived;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [draft, setDraft] = useState({
    fixedAssets: order.figures.fixedAssets,
    currentAssets: order.figures.currentAssets,
    creditorsDueWithinOneYear: order.figures.creditorsDueWithinOneYear,
    creditorsDueAfterOneYear: order.figures.creditorsDueAfterOneYear,
    calledUpShareCapital: order.figures.calledUpShareCapital,
    profitAndLossAccount: order.figures.profitAndLossAccount,
    netAssets: order.figures.netAssets,
  });
  // Variant: 'paid' = cash at bank (currentAssets > 0),
  // 'unpaid' = called-up share capital not paid (currentAssets = 0,
  // share capital IS the asset side). Derived from current draft.
  const variant: 'paid' | 'unpaid' =
    draft.currentAssets === 0 && draft.calledUpShareCapital > 0 ? 'unpaid' : 'paid';

  function setVariant(v: 'paid' | 'unpaid') {
    if (v === 'unpaid') {
      // Move whatever's in currentAssets over to calledUpShareCapital
      // if the customer had cash + no share capital yet, so we don't
      // lose their number when they flip.
      setDraft((d) => ({
        ...d,
        currentAssets: 0,
        calledUpShareCapital: d.calledUpShareCapital || d.currentAssets || 1,
      }));
    } else {
      // Flipping to paid: mirror share capital onto currentAssets so
      // the balance sheet keeps balancing without extra typing.
      setDraft((d) => ({
        ...d,
        currentAssets: d.calledUpShareCapital || 1,
      }));
    }
  }

  // Derived: for a valid balance sheet, netAssets should equal
  // (assets - liabilities) AND (share capital + P&L). The asset side
  // is treated differently per variant to mirror what the iXBRL
  // generator will actually produce.
  const assetSide = variant === 'unpaid'
    ? draft.fixedAssets + draft.calledUpShareCapital
    : draft.fixedAssets + draft.currentAssets;
  const derivedAssets = assetSide - draft.creditorsDueWithinOneYear - draft.creditorsDueAfterOneYear;
  const derivedEquity = draft.calledUpShareCapital + draft.profitAndLossAccount;
  const balances = derivedAssets === draft.netAssets && derivedEquity === draft.netAssets;

  function num(field: keyof typeof draft, value: string) {
    // Empty string → 0; anything else → integer (rounded, no decimals
    // in dormant filings). Accept negative for P&L only.
    const n = value === '' ? 0 : Math.round(Number(value));
    if (Number.isNaN(n)) return;
    setDraft((d) => ({ ...d, [field]: n }));
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await adminApi.updateFigures(order.id, draft);
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setDraft({
      fixedAssets: order.figures.fixedAssets,
      currentAssets: order.figures.currentAssets,
      creditorsDueWithinOneYear: order.figures.creditorsDueWithinOneYear,
      creditorsDueAfterOneYear: order.figures.creditorsDueAfterOneYear,
      calledUpShareCapital: order.figures.calledUpShareCapital,
      profitAndLossAccount: order.figures.profitAndLossAccount,
      netAssets: order.figures.netAssets,
    });
    setEditing(false);
    setError(null);
  }

  const money = (n: number) => `£${n.toLocaleString('en-GB')}`;

  return (
    <div className="rounded-xl bg-white p-5 ring-1 ring-slate-200">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Balance sheet</h3>
        {editable && !editing && (
          <button
            onClick={() => setEditing(true)}
            className="text-xs font-semibold text-brand-700 hover:underline"
          >
            Edit figures
          </button>
        )}
      </div>

      {!editing ? (
        <dl className="mt-3 space-y-1.5 text-sm">
          <Row k="Fixed assets" v={money(order.figures.fixedAssets)} />
          <Row k="Current assets" v={money(order.figures.currentAssets)} />
          <Row k="Creditors (≤1y)" v={money(order.figures.creditorsDueWithinOneYear)} />
          <Row k="Creditors (>1y)" v={money(order.figures.creditorsDueAfterOneYear)} />
          <Row k="Net assets" v={money(order.figures.netAssets)} bold />
          <Row k="Share capital" v={money(order.figures.calledUpShareCapital)} />
          <Row k="P&L account" v={money(order.figures.profitAndLossAccount)} />
        </dl>
      ) : (
        <div className="mt-3 space-y-3 text-sm">
          {/* Variant selector — makes the paid-vs-unpaid choice
              explicit rather than making the admin infer it from
              whether Current assets is £0. Flipping this rearranges
              the balance sheet layout in the resulting iXBRL. */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Share capital status
            </div>
            <div className="mt-1.5 inline-flex rounded-lg bg-slate-100 p-0.5">
              <button
                type="button"
                onClick={() => setVariant('paid')}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                  variant === 'paid'
                    ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Paid in cash
              </button>
              <button
                type="button"
                onClick={() => setVariant('unpaid')}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                  variant === 'unpaid'
                    ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Subscribed but not paid
              </button>
            </div>
            <p className="mt-1.5 text-xs text-slate-500">
              {variant === 'paid'
                ? 'Balance sheet will show "Cash at bank and in hand" as the asset.'
                : 'Balance sheet will show "Called-up share capital not paid" as the asset (Current assets locked to £0).'}
            </p>
          </div>

          <EditableRow label="Fixed assets" value={draft.fixedAssets} onChange={(v) => num('fixedAssets', v)} />
          {variant === 'paid' ? (
            <EditableRow label="Cash at bank" value={draft.currentAssets} onChange={(v) => num('currentAssets', v)} />
          ) : (
            <div className="flex items-center justify-between gap-3 text-slate-400">
              <span>Cash at bank</span>
              <span className="text-xs italic">n/a (unpaid variant)</span>
            </div>
          )}
          <EditableRow label="Creditors (≤1y)" value={draft.creditorsDueWithinOneYear} onChange={(v) => num('creditorsDueWithinOneYear', v)} />
          <EditableRow label="Creditors (>1y)" value={draft.creditorsDueAfterOneYear} onChange={(v) => num('creditorsDueAfterOneYear', v)} />
          <EditableRow label="Net assets" value={draft.netAssets} onChange={(v) => num('netAssets', v)} bold />
          <EditableRow
            label={variant === 'paid' ? 'Called-up share capital' : 'Called-up share capital (not paid)'}
            value={draft.calledUpShareCapital}
            onChange={(v) => num('calledUpShareCapital', v)}
          />
          <EditableRow label="P&L account" value={draft.profitAndLossAccount} onChange={(v) => num('profitAndLossAccount', v)} allowNegative />

          {!balances && (
            <div className="mt-2 rounded-md bg-amber-50 p-2 text-xs text-amber-800">
              <strong>Balance check:</strong> Assets − Liabilities = £{derivedAssets.toLocaleString('en-GB')}
              {' '}· Share capital + P&amp;L = £{derivedEquity.toLocaleString('en-GB')}
              {' '}· Net assets on file = £{draft.netAssets.toLocaleString('en-GB')}
              {' '}— these should all match before you save.
            </div>
          )}
          {error && (
            <div className="mt-2 rounded-md bg-red-50 p-2 text-xs text-red-700">{error}</div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              onClick={save}
              disabled={saving}
              className="btn-primary text-sm"
            >
              {saving ? 'Saving…' : 'Save figures'}
            </button>
            <button
              onClick={cancel}
              disabled={saving}
              className="text-sm font-medium text-slate-600 hover:text-slate-900"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                setPreviewBusy(true);
                setError(null);
                try {
                  await adminApi.previewIxbrl({
                    companyNumber: order.companyNumber,
                    companyName: order.companyName,
                    directorName: order.directorName || undefined,
                    periodStart: order.periodStart,
                    periodEnd: order.periodEnd,
                    isFirstFiling: order.isFirstFiling,
                    figures: draft,
                  });
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Preview failed');
                } finally {
                  setPreviewBusy(false);
                }
              }}
              disabled={previewBusy}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              {previewBusy ? 'Rendering…' : '👁 Preview iXBRL'}
            </button>
            <span className="ml-2 text-xs text-slate-500">
              Preview opens the current draft in a new tab — no save needed.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function EditableRow({
  label,
  value,
  onChange,
  bold = false,
  allowNegative = false,
}: {
  label: string;
  value: number;
  onChange: (v: string) => void;
  bold?: boolean;
  allowNegative?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <label className={`text-slate-600 ${bold ? 'font-semibold text-slate-900' : ''}`}>{label}</label>
      <div className="flex items-center gap-1">
        <span className="text-slate-500">£</span>
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          min={allowNegative ? undefined : 0}
          step={1}
          className={`w-28 rounded-md border border-slate-200 px-2 py-1 text-right tabular-nums focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ${bold ? 'font-semibold' : ''}`}
        />
      </div>
    </div>
  );
}

/**
 * Strip of operator-only actions at the top of the order detail page:
 *
 *   - Resend receipt: re-send the payment receipt email to the customer.
 *     Available once the order is paid.
 *   - Refund:        issue a Stripe refund (full or partial). Confirms
 *                    before charging so we don't fat-finger it.
 *
 * Resubmit lives in its own block below — it needs the auth code so it
 * doesn't fit the one-click model.
 */
function QuickActions({
  order,
  onChange,
}: {
  order: AdminOrder;
  onChange: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  const paid =
    order.status !== 'PENDING_PAYMENT' && order.status !== 'REJECTED'
      ? true
      : order.status === 'REJECTED' && !!order.paidAt;
  // We allow resending receipt for any non-pending order.
  const canResendReceipt = order.status !== 'PENDING_PAYMENT';
  // Refund needs the Stripe payment intent. Hidden once the filing is
  // ACCEPTED — at that point the customer got what they paid for, so
  // refunding would need to be an explicit goodwill decision (not a
  // one-click button on a happy-path order).
  const canRefund = !!order.stripePaymentIntentId && paid && order.status !== 'ACCEPTED';
  // Filing outcome email — only for REJECTED (rejection notice) or
  // ACCEPTED with manual filing where we haven't emailed yet. We hide
  // the rejection variant once the order flips to ACCEPTED because
  // sending "your filing was rejected" to a now-accepted customer is
  // wrong.
  const canSendFilingEmail = order.status === 'ACCEPTED' || order.status === 'REJECTED';
  // 'Mark as manually filed' only makes sense for orders that aren't already
  // accepted — i.e. anything paid that didn't go through our pipeline.
  const canMarkManuallyFiled =
    order.status !== 'PENDING_PAYMENT' && order.status !== 'ACCEPTED';
  const isArchived = !!order.archived;
  // Test orders (created by local dev with NODE_ENV=development) can be
  // hard-deleted from the DB. Real orders (ord_*) are protected — the
  // backend refuses to delete them.
  const isTestOrder = order.id.startsWith('TEST-');

  async function deleteTestOrder() {
    if (!isTestOrder) return;
    if (
      !confirm(
        `Permanently delete test order ${order.id}?\n\n` +
          'This removes the row from the database — no recovery. ' +
          'Only test orders can be deleted (real orders can only be archived).',
      )
    )
      return;
    setBusy('archive');
    setMsg(null);
    try {
      await adminApi.deleteTestOrder(order.id);
      // Order row is gone — leave the page rather than refetching
      // a 404. Use a full navigation because QuickActions is nested
      // and doesn't have react-router's `navigate`.
      window.location.href = '/admin/filings';
    } catch (err) {
      setMsg({ tone: 'err', text: err instanceof Error ? err.message : 'Delete failed' });
      setBusy(null);
    }
  }

  async function resendReceipt() {
    setBusy('receipt');
    setMsg(null);
    try {
      const r = await adminApi.resendReceipt(order.id);
      setMsg({ tone: 'ok', text: `Payment receipt re-sent to ${r.sentTo}.` });
    } catch (err) {
      setMsg({ tone: 'err', text: err instanceof Error ? err.message : 'Resend failed' });
    } finally {
      setBusy(null);
    }
  }

  async function toggleArchive() {
    if (isArchived) {
      setBusy('archive');
      setMsg(null);
      try {
        await adminApi.unarchiveOrder(order.id);
        setMsg({ tone: 'ok', text: 'Order restored — now visible in the default list.' });
        onChange();
      } catch (err) {
        setMsg({ tone: 'err', text: err instanceof Error ? err.message : 'Restore failed' });
      } finally {
        setBusy(null);
      }
      return;
    }
    const reason = prompt(
      `Archive this order?\n\n` +
        `Archived orders are hidden from the default Filings list and ` +
        `customer detail. Data is retained — you can restore at any ` +
        `time.\n\nReason (optional):`,
      'Test order',
    );
    if (reason === null) return;
    setBusy('archive');
    setMsg(null);
    try {
      await adminApi.archiveOrder(order.id, { reason: reason || undefined });
      setMsg({ tone: 'ok', text: 'Order archived. It no longer appears in default views.' });
      onChange();
    } catch (err) {
      setMsg({ tone: 'err', text: err instanceof Error ? err.message : 'Archive failed' });
    } finally {
      setBusy(null);
    }
  }

  async function markManuallyFiled() {
    const ackPrompt = prompt(
      `Mark this order as ACCEPTED (filed outside our pipeline)?\n\n` +
      `Optional — Companies House acknowledgement number from WebFiling:\n` +
      `(leave blank if you don't have one)`,
      order.chAckNumber || '',
    );
    if (ackPrompt === null) return; // cancelled
    const note = prompt(
      `Optional internal note (e.g. "Filed via WebFiling after gateway rejection")`,
      'Filed manually via Companies House WebFiling',
    );
    if (note === null) return; // cancelled
    setBusy('manual');
    setMsg(null);
    try {
      await adminApi.markManuallyFiled(order.id, {
        chAckNumber: ackPrompt.trim() || undefined,
        note: note.trim() || undefined,
      });
      setMsg({
        tone: 'ok',
        text: 'Order marked as ACCEPTED. Use "Send acceptance email" if the customer still needs notifying.',
      });
      onChange();
    } catch (err) {
      setMsg({ tone: 'err', text: err instanceof Error ? err.message : 'Update failed' });
    } finally {
      setBusy(null);
    }
  }

  async function sendFilingEmail() {
    const which = order.status === 'ACCEPTED' ? 'acceptance' : 'rejection';
    if (!confirm(
      `Send ${which} email to ${order.email}?\n\n` +
      `The customer will receive the standard "${order.status === 'ACCEPTED' ? '✅ Your filing has been accepted' : '⚠️ Filing requires attention'}" email.`,
    )) return;
    setBusy('filing');
    setMsg(null);
    try {
      const r = await adminApi.sendFilingEmail(order.id);
      setMsg({
        tone: 'ok',
        text: `${r.type === 'ACCEPTED' ? 'Acceptance' : 'Rejection'} email sent to ${r.sentTo}.`,
      });
    } catch (err) {
      setMsg({ tone: 'err', text: err instanceof Error ? err.message : 'Send failed' });
    } finally {
      setBusy(null);
    }
  }

  async function refund() {
    const fullAmount = `£${(order.amountPence / 100).toFixed(2)}`;
    if (!confirm(
      `Refund ${fullAmount} to ${order.email}?\n\n` +
      `This issues a Stripe refund against the original card. ` +
      `Stripe's processing fees are not returned by Stripe to us.\n\n` +
      `Click OK to confirm.`,
    )) return;
    setBusy('refund');
    setMsg(null);
    try {
      const r = await adminApi.refundOrder(order.id);
      setMsg({
        tone: 'ok',
        text: `Refund issued (${r.refundId}, status ${r.status}). The customer will see it within 5–10 working days.`,
      });
      onChange();
    } catch (err) {
      setMsg({ tone: 'err', text: err instanceof Error ? err.message : 'Refund failed' });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mt-2 rounded-xl bg-white p-4 ring-1 ring-slate-200">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Quick actions
        </span>
        <span className="flex-1" />
        <button
          onClick={resendReceipt}
          disabled={!canResendReceipt || busy !== null}
          title={!canResendReceipt ? 'Order not paid yet' : 'Re-send the payment receipt email'}
          className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
        >
          {busy === 'receipt' ? 'Sending…' : '✉️ Resend receipt'}
        </button>
        <button
          onClick={toggleArchive}
          disabled={busy !== null}
          title={isArchived ? 'Restore this order to the default list' : 'Hide this order from default views (data retained)'}
          className={`rounded-md border px-3 py-1.5 text-xs font-medium disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-white disabled:text-slate-400 ${
            isArchived
              ? 'border-slate-200 text-slate-700 hover:bg-slate-50'
              : 'border-slate-200 text-slate-500 hover:bg-slate-50'
          }`}
        >
          {busy === 'archive'
            ? (isArchived ? 'Restoring…' : 'Archiving…')
            : (isArchived ? '↩ Restore' : '🗄 Archive')}
        </button>
        {isTestOrder && (
          <button
            onClick={deleteTestOrder}
            disabled={busy !== null}
            title="Permanently delete this test order from the database (test orders only)"
            className="rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-white disabled:text-slate-400"
          >
            🗑 Delete test order
          </button>
        )}
        <button
          onClick={markManuallyFiled}
          disabled={!canMarkManuallyFiled || busy !== null}
          title={
            !canMarkManuallyFiled
              ? 'Already accepted, or not paid yet'
              : 'Mark this order as ACCEPTED (filed via WebFiling or other channel)'
          }
          className="rounded-md border border-green-200 bg-green-50 px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-white disabled:text-slate-400"
        >
          {busy === 'manual' ? 'Marking…' : '📝 Mark as manually filed'}
        </button>
        <button
          onClick={sendFilingEmail}
          disabled={!canSendFilingEmail || busy !== null}
          title={
            !canSendFilingEmail
              ? 'Wait until Companies House accepts or rejects the filing'
              : `Send ${order.status === 'ACCEPTED' ? 'acceptance' : 'rejection'} email to customer`
          }
          className={`rounded-md border px-3 py-1.5 text-xs font-medium disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-white disabled:text-slate-400 ${
            order.status === 'ACCEPTED'
              ? 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
              : order.status === 'REJECTED'
              ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'
              : 'border-slate-200 text-slate-700'
          }`}
        >
          {busy === 'filing'
            ? 'Sending…'
            : order.status === 'ACCEPTED'
            ? '✅ Send acceptance email'
            : order.status === 'REJECTED'
            ? '⚠️ Send rejection email'
            : '✉️ Send filing email'}
        </button>
        {canRefund && (
          <button
            onClick={refund}
            disabled={busy !== null}
            title="Issue a full refund via Stripe"
            className="rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:text-slate-400 disabled:border-slate-200 disabled:bg-white"
          >
            {busy === 'refund'
              ? 'Refunding…'
              : `↪ Refund £${(order.amountPence / 100).toFixed(2)}`}
          </button>
        )}
      </div>
      {msg && (
        <div
          className={`mt-3 rounded-md px-3 py-2 text-xs ${
            msg.tone === 'ok' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'
          }`}
        >
          {msg.text}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    PENDING_PAYMENT: 'bg-amber-50 text-amber-700 ring-amber-200',
    PAID: 'bg-blue-50 text-blue-700 ring-blue-200',
    SUBMITTING: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
    ACCEPTED: 'bg-green-50 text-green-700 ring-green-200',
    REJECTED: 'bg-red-50 text-red-700 ring-red-200',
  };
  return (
    <span className={`rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset ${styles[status] || ''}`}>
      {status}
    </span>
  );
}
